import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from '@/app/navigation';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import type { Conversation, PaginatedResponse } from '@octery/shared';

export function ConversationsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = parseInt(searchParams.get('page') || '1');
  const status = searchParams.get('status') || undefined;

  const { data, isLoading } = useQuery({
    queryKey: ['conversations', { page, status }],
    queryFn: () =>
      api.get<PaginatedResponse<Conversation>>(
        `/api/conversations?page=${page}&pageSize=20${status ? `&status=${status}` : ''}`,
      ),
  });

  const statusColors: Record<string, string> = {
    active: 'bg-blue-100 text-blue-800',
    completed: 'bg-green-100 text-green-800',
    handed_off: 'bg-purple-100 text-purple-800',
    abandoned: 'bg-gray-100 text-gray-800',
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Conversations</h1>
          <p className="text-muted-foreground">
            View and analyze AI intake conversations
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => window.open('/api/conversations/export/csv', '_blank')}
        >
          Export CSV
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        {['all', 'active', 'completed', 'handed_off', 'abandoned'].map((s) => (
          <Button
            key={s}
            variant={(!status && s === 'all') || status === s ? 'default' : 'outline'}
            size="sm"
            onClick={() => {
              if (s === 'all') {
                setSearchParams({});
              } else {
                setSearchParams({ status: s, page: '1' });
              }
            }}
          >
            {s.replace('_', ' ')}
          </Button>
        ))}
      </div>

      {/* Conversations list */}
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-4">
                <div className="h-5 bg-muted rounded w-1/2" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : !data?.items?.length ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">No conversations found</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="space-y-2">
            {data.items.map((conv) => (
              <Card key={conv.id} className="hover:bg-accent/50 transition-colors">
                <a href={`/conversations/${conv.id}`}>
                  <CardContent className="flex items-center justify-between p-4">
                    <div>
                      <p className="font-medium">Contact: {conv.contactId}</p>
                      <p className="text-sm text-muted-foreground">
                        {conv.channel} · {conv.messageCount} messages
                        {conv.lastIntent && ` · Intent: ${conv.lastIntent}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusColors[conv.status] || 'bg-gray-100'}`}
                      >
                        {conv.status}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {new Date(conv.startedAt).toLocaleDateString()}
                      </span>
                    </div>
                  </CardContent>
                </a>
              </Card>
            ))}
          </div>

          {/* Pagination */}
          {data.totalPages > 1 && (
            <div className="flex justify-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() =>
                  setSearchParams({ ...Object.fromEntries(searchParams), page: String(page - 1) })
                }
              >
                Previous
              </Button>
              <span className="flex items-center text-sm text-muted-foreground">
                Page {page} of {data.totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= data.totalPages}
                onClick={() =>
                  setSearchParams({ ...Object.fromEntries(searchParams), page: String(page + 1) })
                }
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
